/**
 * MiniProjectKpiNavigation — Task #225.
 *
 * The 5-tab KPI navigation bar inside the MP detail page's Management
 * tab. Visual parallel to FP's Performance subtabs (KpiSummaryHeader),
 * driven by MP-specific data sources via computeMiniProjectKpiStatus.
 *
 * CSS unification (2026-05-27 follow-up): this component imports
 * KpiSummaryHeader.module.css directly so dimensions, padding, fonts,
 * hover/active states, and responsive breakpoints are identical
 * between FP and MP. Adding a new style in KpiSummaryHeader.module.css
 * propagates to both surfaces automatically — no drift possible.
 *
 * Differences from FP:
 *   - No letter grades on MP (FP-style A+/B/etc. requires score_0_100
 *     which MP doesn't compute yet). The .bottom row carries the shape
 *     only, no value.
 *   - All shapes render as circles for now — MP doesn't compute trend.
 *     When trend wiring lands, swap to defineIndicatorIcon for triangles.
 *
 * Sub-tab content: parent owns the body via the summary/schedule/etc.
 * content props. The Summary sub-tab parks the existing magazine spread
 * until Task #228 assembles the proper Summary content; other sub-tabs
 * show "Coming soon" placeholders until #229–#231 land their content.
 */

import { useState, type ReactNode } from "react";
import type { Dispatch, SetStateAction } from "react";
import {
  kpiColorHex,
  type KpiColor,
  type KpiStatus,
  type KpiTrend,
  type KpiTrends,
} from "../../Pages/MiniProjectDetail/computeKpiStatus";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { describeAdjustment } from "../../Data/displayKpiState";
import type { HseColor } from "../../kpi-engine/hse";
import type { HseCategory } from "../../kpi-engine/missionTypes/types";
import { isColorBlindModeEnabled } from "../../theme/colorBlindMode";
import {
  CircleIcon,
  DuotoneCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DuotoneArrowUpIcon,
  DuotoneArrowDownIcon,
} from "../../icons";
import styles from "../KpiSummaryHeader/KpiSummaryHeader.module.css";

export type KpiSubTabKey = "summary" | "schedule" | "budget" | "mission" | "data";

interface KpiSubTab {
  key: KpiSubTabKey;
  label: string;
  color: KpiColor;
  trend: KpiTrend;
  /** DEV-1141: true when this tab's color was floor-capped by an HSE
   *  incident — drives the FP-style adjustment dot. */
  hseCapped?: boolean;
}

/**
 * DEV-1141: floor-cap a KPI color by the worst HSE card color (worse-of).
 * Mirrors the FP rule in displayKpiState.ts adapted to the MP KpiColor
 * scale: red caps everything; yellow caps green/grey; blue (complete) and
 * an already-red tab are left unchanged by a yellow incident.
 */
function applyHseFloorToKpiColor(base: KpiColor, worst: HseColor | undefined): KpiColor {
  if (!worst || worst === "green") return base;
  if (worst === "red") return "red";
  // worst === 'yellow'
  return base === "green" || base === "grey" ? "yellow" : base;
}

interface Props {
  kpiStatus: KpiStatus;
  /** Content for the Summary sub-tab. Parent owns the body so this
   *  component stays presentational. */
  summaryContent: ReactNode;
  scheduleContent?: ReactNode;
  budgetContent?: ReactNode;
  missionContent?: ReactNode;
  dataContent?: ReactNode;
  /** Controlled active sub-tab (2026-05-27). Optional — when omitted,
   *  the component manages its own state for backward compat. When
   *  provided, the parent owns the state so it survives any remount
   *  of this component (e.g., after sub.refetch redraws the tree).
   *  Skip 2026-05-27: editing a task on the Schedule sub-tab was
   *  snapping the user back to Summary because the post-save refetch
   *  was causing local state to reset; lifting the state up is the
   *  durable fix. */
  activeSubTab?: KpiSubTabKey;
  onActiveSubTabChange?: Dispatch<SetStateAction<KpiSubTabKey>>;
  /** DEV-1141: worst HSE card color for this mini. Floor-caps the Mission
   *  and Summary tab colors (worse-of) and lights the adjustment dot on the
   *  capped tabs — the MP parallel of the FP KpiSummaryHeader floor cap.
   *  Absent or 'green' = no cap. */
  hseWorst?: HseColor;
  /** Which HSE category drove the cap, for the dot's tooltip. */
  hseCappedByCategory?: HseCategory | null;
  /** Per-KPI trend indicators. When a KPI's trend is 'up' or 'down',
   *  the shape swaps from a circle to an arrow (up = improved). When
   *  null or 'flat', the shape stays a circle. Skip 2026-05-27 — see
   *  computeMpKpiTrends for the rules. Omitting the prop is equivalent
   *  to all-null (no trend indicators). */
  kpiTrend?: KpiTrends;
}

export default function MiniProjectKpiNavigation({
  kpiStatus,
  summaryContent,
  scheduleContent,
  budgetContent,
  missionContent,
  dataContent,
  activeSubTab: activeSubTabProp,
  onActiveSubTabChange,
  kpiTrend,
  hseWorst,
  hseCappedByCategory,
}: Props) {
  const { t: tKpi } = useAppTranslation("kpi");
  // Internal fallback for uncontrolled usage. When the parent passes
  // activeSubTab, we ignore this state and treat the prop as the
  // single source of truth — that's how the parent's persistence
  // survives remounts.
  const [internalActiveSubTab, setInternalActiveSubTab] = useState<KpiSubTabKey>("summary");
  const activeSubTab = activeSubTabProp ?? internalActiveSubTab;
  const setActiveSubTab: Dispatch<SetStateAction<KpiSubTabKey>> =
    onActiveSubTabChange ?? setInternalActiveSubTab;

  // DEV-1141: floor-cap Mission + Summary by the worst HSE color (worse-of).
  // Summary inherits the cap because Mission is one of its inputs — same rule
  // FP applies via displayKpiState. Schedule/Budget/Data are unaffected.
  const cappedSummary = applyHseFloorToKpiColor(kpiStatus.summary, hseWorst);
  const cappedMission = applyHseFloorToKpiColor(kpiStatus.mission, hseWorst);

  const subTabs: KpiSubTab[] = [
    { key: "summary",  label: "Summary",          color: cappedSummary,             trend: kpiTrend?.summary         ?? null, hseCapped: cappedSummary !== kpiStatus.summary },
    { key: "schedule", label: "Schedule",         color: kpiStatus.schedule,        trend: kpiTrend?.schedule        ?? null },
    { key: "budget",   label: "Budget",           color: kpiStatus.budget,          trend: kpiTrend?.budget          ?? null },
    { key: "mission",  label: "Mission",          color: cappedMission,             trend: kpiTrend?.mission         ?? null, hseCapped: cappedMission !== kpiStatus.mission },
    { key: "data",     label: "Data Reliability", color: kpiStatus.dataReliability, trend: kpiTrend?.dataReliability ?? null },
  ];

  // Localized tooltip for the HSE floor-cap dot (FP describeAdjustment).
  const hseTooltip = hseCappedByCategory
    ? describeAdjustment(
        { source: "hse_floor_cap", cappedByCategory: hseCappedByCategory },
        tKpi as (key: string, params?: Record<string, string>) => string,
      ) ?? undefined
    : undefined;

  const body: Record<KpiSubTabKey, ReactNode> = {
    summary:  summaryContent,
    schedule: scheduleContent  ?? <ComingSoon label="Schedule" />,
    budget:   budgetContent    ?? <ComingSoon label="Budget" />,
    mission:  missionContent   ?? <ComingSoon label="Mission" />,
    data:     dataContent      ?? <ComingSoon label="Data Reliability" />,
  };

  return (
    <div>
      <div className={styles.row} role="tablist" aria-label="KPI tabs">
        {subTabs.map((t) => {
          const isActive = activeSubTab === t.key;
          const accentColor = kpiColorHex(t.color);
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`${styles.cell} ${isActive ? styles.active : ""}`}
              style={{ ["--cell-accent" as any]: accentColor }}
              onClick={() => setActiveSubTab(t.key)}
              title={t.hseCapped ? hseTooltip : undefined}
            >
              {/* DEV-1141: HSE floor-cap dot — same FP adjustmentBadge as the
                  full-project KpiSummaryHeader, shown on the capped tab. */}
              {t.hseCapped && (
                <span
                  className={styles.adjustmentBadge}
                  aria-label={hseTooltip}
                  role="img"
                >
                  !
                </span>
              )}
              <span className={styles.eyebrow}>{t.label}</span>
              <span className={styles.bottom}>
                <span className={styles.shape} aria-hidden="true">
                  <KpiShape color={t.color} trend={t.trend} />
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div role="tabpanel">{body[activeSubTab]}</div>
    </div>
  );
}

/** KPI shape. Skip 2026-05-27 — trend indicators turn on for the four
 *  status-derived KPIs (Schedule, Budget, Mission, Summary). Up arrow
 *  = improved (better color than the previous status), down arrow =
 *  worsened, circle = flat OR null (no previous status to compare, or
 *  one side is grey). Color-blind mode picks solid icons; standard
 *  mode picks duotone halo icons — same convention FP uses via
 *  ShapeFor. Data Reliability stays a circle because DR isn't
 *  snapshotted per status; trending it needs separate infrastructure
 *  (filed as follow-up). */
function KpiShape({ color, trend }: { color: KpiColor; trend: KpiTrend }) {
  const hex = kpiColorHex(color);
  const solid = isColorBlindModeEnabled();
  if (trend === "up") {
    return solid ? (
      <ArrowUpIcon color={hex} height={36} width={36} />
    ) : (
      <DuotoneArrowUpIcon color={hex} height={36} width={36} />
    );
  }
  if (trend === "down") {
    return solid ? (
      <ArrowDownIcon color={hex} height={36} width={36} />
    ) : (
      <DuotoneArrowDownIcon color={hex} height={36} width={36} />
    );
  }
  return solid ? (
    <CircleIcon color={hex} height={36} width={36} />
  ) : (
    <DuotoneCircleIcon color={hex} height={36} width={36} />
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <div
      style={{
        background: "var(--bg-surface)",
        border: "1px dashed var(--border-default)",
        borderRadius: 8,
        padding: "32px 24px",
        textAlign: "center",
        color: "var(--text-muted)",
        fontSize: 13,
        lineHeight: 1.6,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: "var(--text-primary)",
          marginBlockEnd: 6,
        }}
      >
        {label} sub-tab — coming soon
      </div>
      <div>
        This sub-tab is part of the Management surface refactor. The
        Summary sub-tab shows the consolidated overview today.
      </div>
    </div>
  );
}
